import { SUPPORTED_LANGUAGES } from '../../constants';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';

// localStorage 키 (i18next와 통일)
const LANGUAGE_STORAGE_KEY = 'i18nextLng';

// 사용자 언어 설정 저장
export const saveLanguagePreference = async (languageCode) => {
  try {
    // 웹: localStorage에 저장
    localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
    console.log(`💾 [언어 설정] localStorage에 저장: ${languageCode}`);
    
    // 모바일 앱: Capacitor Preferences에도 저장 (Share Extension 연동)
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({
        key: LANGUAGE_STORAGE_KEY,
        value: languageCode
      });
      console.log(`📱 [언어 설정] 네이티브 저장소에 저장: ${languageCode}`);
    }
  } catch (error) {
    console.error('❌ [언어 설정] 저장 실패:', error);
  }
};

// 사용자 언어 설정 불러오기
export const getLanguagePreference = async () => {
  try {
    // 모바일 앱: Capacitor Preferences 우선
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key: LANGUAGE_STORAGE_KEY });
      if (value && SUPPORTED_LANGUAGES.some(lang => lang.code === value)) {
        console.log(`📱 [언어 설정] 네이티브 저장소에서 읽음: ${value}`);
        return value;
      }
    }
    
    // 웹 또는 폴백: localStorage
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some(lang => lang.code === stored)) {
      return stored;
    }
  } catch (error) {
    console.error('❌ [언어 설정] 읽기 실패:', error);
  }
  return null;
};

// 디바이스 언어 감지 (모바일 앱 전용)
export const detectDeviceLanguage = async () => {
  try {
    const languageCode = await Device.getLanguageCode();
    const deviceLang = languageCode.value; // { value: 'ko-KR' } 형태
    
    // 언어 코드 추출 (예: 'ko-KR' -> 'ko', 'en-US' -> 'en')
    const langCode = deviceLang.split('-')[0];
    
    if (SUPPORTED_LANGUAGES.some(lang => lang.code === langCode)) {
      console.log(`📱 [디바이스 언어감지] ${deviceLang} -> ${langCode}`);
      return langCode;
    }
    
    console.log(`📱 [디바이스 언어감지] 지원하지 않는 언어: ${deviceLang} -> 영어로 폴백`);
    return 'en';
  } catch (error) {
    console.error('❌ [디바이스 언어감지] 실패:', error);
    return null;
  }
};

// IP 기반 지역 감지
export const detectRegionByIP = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    const countryCode = data.country_code?.toLowerCase();
    
    // 국가 코드에 따른 언어 매핑
    const countryToLanguage = {
      'us': 'en',  // 미국 -> 영어
      'gb': 'en',  // 영국 -> 영어
      'ca': 'en',  // 캐나다 -> 영어 (불어도 있지만 기본은 영어)
      'au': 'en',  // 호주 -> 영어
      'nz': 'en',  // 뉴질랜드 -> 영어
      'es': 'es',  // 스페인 -> 스페인어
      'mx': 'es',  // 멕시코 -> 스페인어
      'ar': 'es',  // 아르헨티나 -> 스페인어
      'co': 'es',  // 콜롬비아 -> 스페인어
      'pe': 'es',  // 페루 -> 스페인어
      'cl': 'es',  // 칠레 -> 스페인어
      'de': 'de',  // 독일 -> 독일어
      'at': 'de',  // 오스트리아 -> 독일어
      'fr': 'fr',  // 프랑스 -> 프랑스어
      'be': 'fr',  // 벨기에 -> 프랑스어 (플라망어도 있지만)
      'ch': 'fr',  // 스위스 -> 프랑스어 (독일어, 이탈리아어도 있지만)
      'cn': 'zh',  // 중국 -> 중국어
      'tw': 'zh',  // 대만 -> 중국어
      'hk': 'zh',  // 홍콩 -> 중국어
      'sg': 'zh',  // 싱가포르 -> 중국어 (다언어 국가지만 중국어 비중 높음)
      'jp': 'ja',  // 일본 -> 일본어
      'kr': 'ko',  // 한국 -> 한국어
      'kp': 'ko',  // 북한 -> 한국어
      'in': 'hi',  // 인도 -> 힌디어
      'pk': 'hi',  // 파키스탄 -> 힌디어
      'bd': 'hi'   // 방글라데시 -> 힌디어
    };
    
    const detectedLanguage = countryToLanguage[countryCode];
    
    if (detectedLanguage && SUPPORTED_LANGUAGES.some(lang => lang.code === detectedLanguage)) {
      console.log(`🌍 [IP 언어감지] ${data.country} (${countryCode}) -> ${detectedLanguage}`);
      return detectedLanguage;
    }
    
    // 지원하지 않는 지역은 영어로 폴백
    console.log(`🌍 [IP 언어감지] 지원하지 않는 지역 ${data.country} (${countryCode}) -> 영어로 폴백`);
    return 'en';
  } catch (error) {
    console.error('❌ [IP 언어감지] 실패:', error);
    return null;
  }
};

// 브라우저 언어 감지
export const detectBrowserLanguage = () => {
  try {
    const browserLang = navigator.language || navigator.languages?.[0];
    if (!browserLang) return null;
    
    // 언어 코드 추출 (예: 'ko-KR' -> 'ko', 'en-US' -> 'en')
    const langCode = browserLang.split('-')[0];
    
    if (SUPPORTED_LANGUAGES.some(lang => lang.code === langCode)) {
      console.log(`🌐 [브라우저 언어감지] ${browserLang} -> ${langCode}`);
      return langCode;
    }
    
    console.log(`🌐 [브라우저 언어감지] 지원하지 않는 언어: ${browserLang}`);
    return null;
  } catch (error) {
    console.error('❌ [브라우저 언어감지] 실패:', error);
    return null;
  }
};

// 종합 언어 감지 (우선순위: localStorage > 플랫폼별 감지 > 기본값)
export const detectUserLanguage = async () => {
  console.log('🔍 [언어 감지] 시작...');
  console.log(`🔍 [언어 감지] 플랫폼: ${Capacitor.isNativePlatform() ? '모바일 앱' : '웹 브라우저'}`);
  
  // ========== 1단계: 공통 로직 (저장된 설정) ==========
  const storedLang = await getLanguagePreference();
  if (storedLang) {
    console.log(`✅ [언어 감지] 저장된 설정: ${storedLang}`);
    return storedLang;
  }
  
  // ========== 2단계: 플랫폼별 분기 ==========
  if (Capacitor.isNativePlatform()) {
    // ===== 모바일 앱 전용 로직 =====
    console.log('📱 [언어 감지] 모바일 플랫폼 감지 로직 실행');
    
    // 디바이스 언어 감지
    const deviceLang = await detectDeviceLanguage();
    if (deviceLang) {
      console.log(`✅ [언어 감지] 디바이스 언어: ${deviceLang}`);
      return deviceLang;
    }
    
    // 모바일에서도 IP 감지 시도 (폴백)
    const ipLang = await detectRegionByIP();
    if (ipLang) {
      console.log(`✅ [언어 감지] IP 기반 (모바일 폴백): ${ipLang}`);
      return ipLang;
    }
  } else {
    // ===== 웹 브라우저 전용 로직 =====
    console.log('🌐 [언어 감지] 웹 브라우저 감지 로직 실행');
    
    // IP 기반 지역 감지
    const ipLang = await detectRegionByIP();
    if (ipLang) {
      console.log(`✅ [언어 감지] IP 기반: ${ipLang}`);
      return ipLang;
    }
    
    // 브라우저 언어 감지
    const browserLang = detectBrowserLanguage();
    if (browserLang) {
      console.log(`✅ [언어 감지] 브라우저: ${browserLang}`);
      return browserLang;
    }
  }
  
  // ========== 3단계: 기본값 (공통) ==========
  console.log(`✅ [언어 감지] 기본값: en`);
  return 'en';
};

// 지원되는 언어인지 확인
export const isSupportedLanguage = (languageCode) => {
  return SUPPORTED_LANGUAGES.some(lang => lang.code === languageCode);
};

// 언어별 히스토리 키 생성 (향후 중복 체크용)
export const getHistoryKey = (videoId, language) => {
  return `${videoId}_${language}`;
};

// 히스토리 객체에서 언어 정보 추출
export const getHistoryLanguageInfo = (historyItem) => {
  if (!historyItem) return { code: 'ko', name: '한국어' };
  
  return {
    code: historyItem.language || 'ko',
    name: historyItem.languageName || '한국어'
  };
};

// 언어 변경 시 히스토리 업데이트가 필요한지 확인
export const shouldUpdateHistoryLanguage = (historyItem, currentLanguage) => {
  if (!historyItem) return false;
  
  const historyLang = historyItem.language || 'ko';
  return historyLang !== currentLanguage;
}; 